// Big lane so a single spin (≤45 steps) always lands well inside the
// rendered range. After each spin we snap-back to INITIAL_CENTER_INDEX
// invisibly — all tiles share the same image, so the user can never
// tell that the lane reset.
//
// MUST be odd so the lane has an exact centre tile under
// `justify-center`. With an even count the viewport centres on the
// gap BETWEEN two tiles, and every integer-step spin lands the same
// way → the wheel "stops between cards".
export const TILE_COUNT = 91;
// DERIVED, never hand-set: `justify-center` rests the strip's GEOMETRIC
// MIDDLE tile under the viewport, and the spin lands `centre + spinSteps`
// there — so the centre index must BE that middle, or the wheel stops on the
// wrong tile while the real pull is pinned off-centre. Tie it to TILE_COUNT
// so the two can never drift apart.
export const INITIAL_CENTER_INDEX = (TILE_COUNT - 1) / 2;

// "Open Next Pack" card reel — its own, larger lane, SAME invariant. Odd so
// there's an exact middle; the centre is DERIVED from the count for the same
// reason as above. Sized so the max landing (centre + MAX_SPIN_STEPS) still
// leaves 5 spin_hold extend batches (EXTEND_STEPS each) of runway:
// (189-1)/2 = 94 ≥ 44 + 5×10.
export const CARD_TILE_COUNT = 189;
export const CARD_CENTER_INDEX = (CARD_TILE_COUNT - 1) / 2;

// Card footprint shared by the winner card (WinnerContent) and the shuffle
// reel tiles (CardSlotTile) so the rounded-corner clip is IDENTICAL — the
// reel→winner landing must show the same rounded card. The box aspect mirrors
// the 232/367 card box; the radius rounds the card's real (object-contain)
// rect, trimming the source PNG's corner triangles.
export const CARD_BOX_ASPECT = 232 / 367;
// 18px on a full-size card (box ≥ ~257px wide); on smaller cards (short
// viewports shrink the scene) the `cqw` term takes over and the radius
// scales with the card instead of over-rounding a tiny thumbnail. `cqw`
// resolves against the card-box div itself — both WinnerContent and
// CardSlotTile mark it `containerType: 'size'`, so the value is identical
// on the reel and the winner (no visual jump at landing).
export const CARD_CORNER_RADIUS = 'min(18px, 7cqw)';

export const MIN_SPIN_MS = 3400;
export const MAX_SPIN_MS = 5600;
// More steps than feels intuitive — real slots spin 3-5 full wheel
// revolutions before braking. Anything under ~20 steps reads as
// "carousel scroll", not "slot machine".
export const MIN_SPIN_STEPS = 28;
export const MAX_SPIN_STEPS = 44;

// Single smooth velocity curve for the main travel: rapid mid, gentle
// shoulders. ONE curve, not multi-segment — keyframe boundaries with
// different ease functions read as a velocity jerk (a slow crawl that
// then "suddenly speeds up"), which is exactly what a three-phase
// accel→cruise→decel attempt reintroduced. The cure for "too fast /
// skips a couple cards" is a longer spin DURATION (more time on the
// gentle decel tail), NOT segmenting the curve. The big card reel gets
// that extra time via CARD_SPIN_MS_SCALE below.
export const SLOT_CURVE: [number, number, number, number] = [0.32, 0, 0.16, 1];
// Rebound after the wheel overshoots — short, gently-resolving curve
// for a mechanical "plonk" feel rather than a rubber-band bounce.
export const REBOUND_EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];
// Wheel overshoots its landing tile by this fraction of a tile-step,
// then snaps back. Classic mechanical-slot tell.
export const OVERSHOOT_FRACTION = 0.14;
// Fraction of total duration the rebound takes. Short = snappy
// "plonk"; long = bouncy.
export const REBOUND_T = 0.08;

// "Skip" — the player fast-forwards the spin. The lane decelerates from
// wherever it currently is straight to the landed tile over this window
// (reusing the decel curve), so the stop still feels mechanical, not a
// teleport.
export const SKIP_DECEL_MS = 460;

// The card reel's tiles are ~1.7× the ready lane's, so the same travel
// reads noticeably faster (more px/s). Stretch its spin so the cruise
// stays readable on the bigger cards.
export const CARD_SPIN_MS_SCALE = 1.3;

// Manual flick — the player can scroll the lane with a finger / mouse
// instead of pressing Shuffle, stop on a pack and tap it to open. The
// scroll is infinite: the drag offset is wrapped modulo one tile step
// (see useLaneDrag.wrapLaneX), all tiles show the same image, so the
// finite lane never runs out of runway and needs no drag constraints.
// Inertia feel of the flick: `power` scales how far a given release
// velocity glides, `timeConstant` how quickly the glide decays. Tuned to
// read as a light physical reel, not the heavy Shuffle slot spin.
export const DRAG_POWER = 0.6;
export const DRAG_TIME_CONSTANT = 250;
// Catching the reel mid-glide (pointer-down freezes the inertia) and
// releasing without starting a new drag leaves the lane between tiles —
// Motion fires no drag callbacks for such a press, so useLaneDrag runs
// its own corrective snap to the nearest pack centre over this window.
export const CATCH_SNAP_MS = 250;
// Dim/undim fade of the resting brightness hierarchy when a drag starts
// or the lane settles — quick enough to feel immediate, slow enough not
// to flicker on every touch.
export const DIM_FADE_MS = 200;
// Sleek: horizontal travel (px) that counts as "swipe to rip" on the lone
// pack — long enough that a stray touch while scrolling doesn't fire it.
export const SWIPE_RIP_PX = 40;

// Settle pause after the wheel stops, before the neighbour-clear fade.
// Gives the eye a beat on the landed tile.
export const SETTLE_MS = 320;

// "Unveil" phase — between Shuffle press and spin start.
export const UNVEIL_MS = 500;

// Neighbour-clear phase — after the wheel stops + settles, the two
// peeking neighbours fade their opacity to 0 so only the landed pack
// remains. Figma 735:53017.
export const CLEAR_TRANSITION_MS = 500;

// Pause between the cleared state and the reveal flash. A short beat on
// the lone pack — trimmed from 1000ms so the payoff arrives while the
// decel tension is still hot instead of dissipating into dead air.
export const REVEAL_DELAY_MS = 450;

// Crossfade window — landed pack fades out while the white glow
// square fades in. Same duration for both so they pivot at 50%.
export const REVEAL_FADE_MS = 500;

// Rotation speed of the sun-burst ray halo. Very slow, uniform
// linear motion — at full-screen scale anything quicker reads as
// flashing rather than gentle radiance.
export const RAYS_ROTATION_S = 60;
// Blur applied to the rotating rays so they read as soft beams of
// light, not crisp pie slices. The conic-gradient stops are wide
// enough that a heavy blur won't wash them away entirely.
export const RAYS_BLUR_PX = 24;

// "Expand" phase — after the glow square has appeared and the
// rarity tint has flashed, a blurred white plate scales up from
// pack-size to fill the screen. Acts as a wipe between ReadyStep
// and WinnerStep so the pack-to-card swap is hidden behind a beat
// of soft white. Long-ish delay so the rotating rays + tint have
// time to read before the wipe takes over; long expand duration
// (~1.5 s) so the swell feels gentle, not flashy.
// Halved from 2000ms — the steady glow + rotating rays were the single
// worst stretch of dead time (nothing escalating, just waiting). 1000ms
// still lets the rarity tint wash mostly bloom before the wipe expands.
export const EXPAND_DELAY_MS = 1000;
export const EXPAND_DURATION_MS = 1500;
// Scale factor that's guaranteed to overrun the viewport given the
// wipe plate's starting footprint (~210 px wide). 210 × 12 = 2520 px,
// plus the 32 px outer blur halo — covers any realistic iframe in
// both axes.
export const EXPAND_SCALE = 12;

// White-veil fade-out — once the wipe has filled the screen we mount
// WinnerStep behind it and fade it out so the card materialises in
// place of the white.
export const WHITE_FADE_MS = 500;

// Hover-tilt on the focal pack — subtle interactivity hint while the
// wheel is at rest. Reference: simey/hover-tilt + Pokemon-cards-css,
// but stripped down: no holographic / foil overlays, just a small
// rotate-towards-cursor + radial glare.
export const MAX_TILT_DEG = 7;
export const HOVER_SCALE = 1.02;
export const TILT_PERSPECTIVE_PX = 900;

// Per-distance brightness when the lane is at rest. Centre stays at
// 1; neighbours progressively darken so the focal pack reads as the
// hero (Figma 744:71985).
export function dimFor(distance: number): number {
  if (distance === 0) return 1;
  if (distance === 1) return 0.6;
  if (distance === 2) return 0.4;
  return 0.28;
}

export function randInt(min: number, max: number): number {
  return Math.floor(min + Math.random() * (max - min + 1));
}

// Each extend batch scrolls this many extra tiles at constant speed while
// waiting for the backend to respond after the natural spin ends.
export const EXTEND_STEPS = 10;
// Duration per extend batch in ms — sets the scrolling speed.
export const EXTEND_BATCH_MS = 2000;

export type AnimMode =
  | 'idle'
  | 'unveil'
  | 'spin'
  | 'skip'
  // Post-spin "keep scrolling" phase: reel continues at constant velocity
  // while outcome is still pending so it never freezes on a card.
  | 'spin_hold'
  | 'cleared'
  | 'reveal'
  | 'expand'
  | 'aborting'
  | 'errored';
